import React, { useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import {useTranslation} from 'react-i18next'
import api from '../../api'

const MAX_RECONNECT_ATTEMPTS = 10

const BalanceCard = ({cid, botId, name, isTotal = false}) => {
  const {t} = useTranslation()

  const wsName = isTotal ? 'Total balances' : `Bot "${name}" balance`

  const ws = useRef()
  const connectionAttempt = useRef(0)
  const retry = useRef(true)
  const [isClosed, setIsClosed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [balances, setBalances] = useState()
  const balancesRef = useRef([])

  const connect = () => {
    setIsLoading(true)
    ws.current = isTotal ? api.initBalancesAllWS(cid) : api.initBalanceWS(cid, botId)

    ws.current.onopen = () => {
      connectionAttempt.current = 0
      retry.current = true
      console.log(`${wsName} WS is connected`)
      setIsLoading(false)
    }

    ws.current.onerror = error => {
      console.error(`${wsName} WS error: `, error)
      ws.current?.close()
    }
  
    ws.current.onclose = event => {
      reconnect(event)
    }

    ws.current.onmessage = event => {
      console.log(`${wsName} WS message: `, event)
      if (event.data === 'ping') {
        return ws.current.send('pong')
      }
      if (event.data === 'pong') return
      
      try {
        const msg = JSON.parse(event.data)

        if (msg.type === 'snapshot') {
          return setBalances(msg.balances)
        }
        if (msg.type === 'update') {
          const balancesUpdated = balancesRef.current.filter(b => !msg.balances.find(bNew => b['symbol'] === bNew['symbol'])).concat(msg.balances)
          setBalances(balancesUpdated)
        }
      } catch {
        console.error(`${wsName} WS message "${event.data}" can't be parsed as JSON`)
      }
    }
  }

  const reconnect = event => {
    console.warn(`${wsName} WS error: ${event.code} ${event.reason}`)

    if (!retry.current) {
      console.error(`${wsName} WS closed`)
      close()
      return
    }
    if (event.code === 4400 && event.code === 4401 && event.code === 4500) {
      console.error(`${wsName} WS closed: ${event.reason}`)
      return close()
    }
    if (connectionAttempt.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error(`${wsName} WS error: max reconnect attempts reached. closing`)
      close()
      return
    }

    const base = 500, max = 30000
    const jitter = Math.random() * 1000
    const delay = Math.min(base * 2 ** connectionAttempt.current + jitter, max)

    console.warn(`${wsName} WS: trying to reconnect in ${(delay/1000).toPrecision(2)}s, attempt ${connectionAttempt.current + 1}`)

    setTimeout(() => {
      connectionAttempt.current = connectionAttempt.current + 1
      connect()
    }, delay)
  }

  const close = () => {
    retry.current = false
    ws.current.close()
    ws.current = null
    setIsClosed(true)
  }

  useEffect(() => {
    connect()

    return () => {
      return close()
    }
  }, [])

  // balancesRef needed becasue "balances" state is always undefined inside "onmessage" handler
  // https://react.dev/learn/state-as-a-snapshot
  useEffect(() => {
    balancesRef.current = balances
  }, [balances])

  return ws.current && (
    <>
      {!isClosed && isTotal && <div className='total'>
        <div className='card' style={{maxWidth: '360px'}}>
          {!isLoading && balances && balances.length > 0 ? <>
            <div className='subtitle'>{t('balances.total')}:</div>
            <ul>
              {_.map(balances, (balance, key) => (
                <li key={key}>
                  <span className='symbol'>{balance.symbol}</span>
                  <span className='amount'>{balance.amount}</span>
                </li>
              ))}
            </ul>
          </> : <div style={{color: '#fff', marginLeft: '8px', padding: '20px'}}>{t('balances.loading')}</div>}
        </div>
      </div>}

      {!isClosed && !isTotal &&
          <div className='card'>
            {!isLoading && balances && balances.length > 0 ? <>
              <p className='subtitle'>{name && name}</p>
              <ul>
                {
                  _.map(balances, (balance, key) => (
                      <li key={key}>
                        <span className='symbol'>{balance.symbol}</span>
                        <span className='amount'>{balance.amount}</span>
                      </li>
                    ))}
              </ul>
            </>
            : <div style={{color: '#fff', marginLeft: '8px', padding: '20px'}}>{t('balances.loading')}</div>}
          </div>
        }
    </>
  )
}

export default BalanceCard